import { DragDropModule } from '@angular/cdk/drag-drop';
import { TextFieldModule } from '@angular/cdk/text-field';
import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { RouterModule } from '@angular/router';
import { ChatModule } from '../chat/chat.module';
import { NgxMaterialTimepickerModule } from 'ngx-material-timepicker';
import { AiAssistantDialogComponent } from './components/ai-assistant-dialog/ai-assistant-dialog.component';
import { AiDialogComponent } from './components/ai-dialog/ai-dialog.component';
import { AllergyListCreatorComponent } from './components/allergy-list-creator/allergy-list-creator.component';
import { AudioUploadComponent } from './components/audio-upload/audio-upload.component';
import { AuthListCreatorComponent } from './components/auth-list-creator/auth-list-creator.component';
// B3 of tasks/theatre-booking-and-procedure-lifecycle.md — the table-backed authorisation list.
// Sits BESIDE AuthListCreatorComponent, which still edits the legacy JSON blob: the backfill
// (scripts/backfill-patient-authorizations.js) has not been run, so the blob is still the only
// place most practices' authorisations live.
import { AuthorizationListComponent } from './components/auth-list-creator/authorization-list.component';
import { AutoLogoutComponent } from './components/auto-logout/auto-logout.component';
import { BackgroundAnimationComponent } from './components/background-animation/background-animation.component';
import { BulkEmailListCreatorComponent } from './components/bulk-email-list-creator/bulk-email-list-creator.component';
import { DashboardComponent, EmailVerificationSnackbarComponent } from './components/dashboard/dashboard.component';
import { DemoAccountGeneratorComponent } from './components/demo-account-generator/demo-account-generator.component';
// B3 of tasks/patient-forms.md — the ONE practice-forms renderer (§5). Declared AND exported: the
// invite wizard, the public /patient-form/:token page and the patient-file panel all render this
// same component, which is the whole reason the feature does not grow a second renderer.
import { DynamicFormRendererComponent } from './components/dynamic-form-renderer/dynamic-form-renderer.component';
import { FabMenuComponent } from './components/fab-menu/fab-menu.component';
import { FileGalleryComponent } from './components/file-gallery/file-gallery.component';
import { FileUploadComponent } from './components/file-upload/file-upload.component';
import { HeaderComponent } from './components/header/header.component';
import { ImageDialogComponent } from './components/image-dialog/image-dialog.component';
import { InfoDialogComponent } from './components/info-dialog/info-dialog.component';
import { AnnouncementDialogComponent } from './components/announcement-dialog/announcement-dialog.component';
import { SystemStatusDotComponent } from './components/system-status-dot/system-status-dot.component';
import { IntlPhoneInputComponent } from './components/intl-phone-input/intl-phone-input.component';
import { SearchPipe } from './components/intl-phone-input/search.pipe';
import { LoaderComponent } from './components/loader/loader.component';
import { MedicalHistoryListCreatorComponent } from './components/medical-history-list-creator/medical-history-list-creator.component';
import { MedicalTeamListCreatorComponent } from './components/medical-team-list-creator/medical-team-list-creator.component';
import { MedicationAutocompleteComponent } from './components/medication-autocomplete/medication-autocomplete.component';
import { MedicationListCreatorComponent } from './components/medication-list-creator/medication-list-creator.component';
import { PageNotFoundComponent } from './components/page-not-found/page-not-found.component';
import { PolicyViewerComponent } from './components/policy-viewer/policy-viewer.component';
import { ProcedureCodeSelectorComponent } from './components/procedure-code-selector/procedure-code-selector.component';
import { RecordingComponent } from './components/recording/recording.component';
import { SidenavComponent } from './components/sidenav/sidenav.component';
import { SignaturePadComponent } from './components/signature-pad/signature-pad.component';
import { TranscriptInputComponent } from './components/transcript-input/transcript-input.component';
import { TranscriptProcessingComponent } from './components/transcript-processing/transcript-processing.component';
import { MatTabGroupGestureDirective } from './directives/mat-tab-group-gesture.directive';
import { NumberOnlyDirective } from './directives/number-only.directive';
import { WhatsappDialogTriggerDirective } from './directives/whatsapp-dialog-trigger.directive';
import { DraggableOverlayPaneDirective } from './directives/draggable-overlay-pane.directive';
import { ResizableOverlayPaneDirective } from './directives/resizable-overlay-pane.directive';
import { AgeFromDatePipe } from './pipes/age-from-date.pipe';
import { PatientHeadingPipe } from './pipes/patient-heading.pipe';
import { DateDifferencePipe } from './pipes/date-difference.pipe';
import { DateFormatPipe } from './pipes/date-format.pipe';
import { FormatMessagePipe } from './pipes/format-message.pipe';
import { HeightInputDirective } from './directives/height-input.directive';
import { WeightInputDirective } from './directives/weight-input.directive';
import { PasswordStrengthComponent } from './components/password-strength/password-strength.component';
import { TutorialCardComponent } from './components/tutorial-dialog/tutorial-dialog.component';
import { TutorialExpansionTargetDirective } from './directives/tutorial-expansion-target.directive';
import { TutorialHighlightTargetDirective } from './directives/tutorial-highlight-target.directive';
import { TutorialFormTargetDirective } from './directives/tutorial-form-target.directive';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { SafeUrlPipe } from './pipes/safe-url.pipe';
import { VideoDialogComponent } from './components/video-dialog/video-dialog.component';
import { MatRadioModule } from '@angular/material/radio';
import { MatStepperModule } from '@angular/material/stepper';
import { HelpIndexDialogComponent } from './components/help-index-dialog/help-index-dialog.component';
import { RecoverRecordingDialogComponent } from './components/recover-recording-dialog/recover-recording-dialog.component';
import { CodeSearchSelectorComponent } from './components/code-search-selector/code-search-selector.component';
import { HistoryChartDialogComponent } from './components/history-chart-dialog/history-chart-dialog.component';
import { NgxChartsModule } from '@swimlane/ngx-charts';

import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { CuraFormReviewDialogComponent } from './components/cura-form-review-dialog/cura-form-review-dialog.component';
import { NgxColorsModule } from 'ngx-colors';
import { PatientActiveDirective } from './directives/patient-active.directive';
import { PatientValidationStatusDirective } from './directives/patient-validation-status.directive';
import { PatientValidationMessagesComponent } from './components/patient-validation-messages/patient-validation-messages.component';
import { GroupSpecialtyHideDirective } from './directives/group-specialty-hide.directive';
import { GroupSpecialtyHideStructuralDirective } from './directives/group-specialty-hide-structural.directive';
import { ProviderInitialsTooltipDirective } from './directives/provider-initials-tooltip.directive';
import { TouchEditDirective } from './directives/touch-edit.directive';
import { SignatureOptionsComponent } from './components/signature-options/signature-options.component';
import { SignatureWrapperComponent } from './components/signature-wrapper/signature-wrapper.component';
import { RecipientSearchComponent } from './components/recipient-search/recipient-search.component';
import { FailedRecordingsDialogComponent } from './components/failed-recordings-dialog/failed-recordings-dialog.component';
import { LabTestSelectorComponent } from './components/lab-test-selector/lab-test-selector.component';
import { LabResultsDialogComponent } from './components/lab-results-dialog/lab-results-dialog.component';
import { LabMonitoringDialogComponent } from './components/lab-monitoring-dialog/lab-monitoring-dialog.component';
import { WhatsAppChatDialogComponent } from './components/whatsapp-chat-dialog/whatsapp-chat-dialog.component';
import { MediaSelectorDialogComponent } from './components/media-selector-dialog/media-selector-dialog.component';
import { DocumentAttachmentPickerComponent } from './components/document-attachment-picker/document-attachment-picker.component';
import { DocumentAttachmentDialogComponent } from './components/document-attachment-picker/document-attachment-dialog.component';
import { PrescriptionSelectorDialogComponent } from './components/prescription-selector-dialog/prescription-selector-dialog.component';
import { BillingDocumentSelectorDialogComponent } from './components/billing-document-selector-dialog/billing-document-selector-dialog.component';
import { LabOrderConfigDialogComponent } from './components/lab-order-config-dialog/lab-order-config-dialog.component';
import { RestoreTimelineDialogComponent } from './components/restore-timeline-dialog/restore-timeline-dialog.component';
import { ExportProgressDialogComponent } from './components/export-progress-dialog/export-progress-dialog.component';
import { ConsumableOverrideSelectorComponent } from './components/consumable-override-selector/consumable-override-selector.component';
import { MatDividerModule } from '@angular/material/divider';
import { TurnstileComponent } from './components/turnstile/turnstile.component';
import { PatientSelectionDialogComponent } from './components/patient-selection-dialog/patient-selection-dialog.component';
import { UnlinkedResultLinkDialogComponent } from './components/unlinked-result-link-dialog/unlinked-result-link-dialog.component';
import { UnlinkedResultDismissDialogComponent } from './components/unlinked-result-dismiss-dialog/unlinked-result-dismiss-dialog.component';
import { TaskDialogComponent } from './components/task-dialog/task-dialog.component';
import { UnlinkedResultPayloadDialogComponent } from './components/unlinked-result-payload-dialog/unlinked-result-payload-dialog.component';
import { DocumentReviewDialogComponent } from './components/document-review-dialog/document-review-dialog.component';
import { FindReplacePanelComponent } from './components/find-replace-panel/find-replace-panel.component';
import { DateRangeMenuComponent } from './components/date-range-menu/date-range-menu.component';
// Risk management (tasks/risk-management.md E2/F2/E3/F3). Shared because BOTH the AI request
// detail and the ward-round note detail render the same suggestions and open the same
// confirm-before-add dialog; R4's controls live in one copy, not two.
import { RiskSuggestionListComponent } from './components/risk-suggestion-list/risk-suggestion-list.component';
import { RiskItemAddDialogComponent } from './components/risk-suggestion-list/risk-item-add-dialog.component';
import { FindReplaceDirective } from './directives/find-replace.directive';
import { KeepCaretVisibleDirective } from './directives/keep-caret-visible.directive';
import { FitAboveKeyboardDirective } from './directives/fit-above-keyboard.directive';
import { FindReplaceScopeDirective } from './directives/find-replace-scope.directive';

const MAT_MODULES = [
  MatButtonModule,
  MatButtonToggleModule,
  MatCheckboxModule,
  MatFormFieldModule,
  MatInputModule,
  MatIconModule,
  MatProgressSpinnerModule,
  MatSnackBarModule,
  MatCardModule,
  MatDialogModule,
  MatListModule,
  MatSidenavModule,
  MatToolbarModule,
  MatMenuModule,
  MatTooltipModule,
  MatSelectModule,
  MatAutocompleteModule,
  MatChipsModule,
  MatTabsModule,
  MatBadgeModule,
  MatSlideToggleModule,
  TextFieldModule,
  MatDatepickerModule,
  MatExpansionModule,
  MatProgressBarModule,
  MatRadioModule,
  MatStepperModule,
  MatDividerModule
];

const PIPES = [
  AgeFromDatePipe,
  PatientHeadingPipe,
  DateDifferencePipe,
  DateFormatPipe,
  FormatMessagePipe,
  SafeUrlPipe
];


@NgModule({
  declarations: [
    TranscriptProcessingComponent,
    HeaderComponent,
    LoaderComponent,
    BackgroundAnimationComponent,
    ImageDialogComponent,
    DashboardComponent,
    SidenavComponent,
    FabMenuComponent,
    ...PIPES,
    InfoDialogComponent,
    AnnouncementDialogComponent,
    SystemStatusDotComponent,
    FileUploadComponent,
    ProcedureCodeSelectorComponent,
    SignaturePadComponent,
    FileGalleryComponent,
    AiDialogComponent,
    AudioUploadComponent,
    RecordingComponent,
    TranscriptInputComponent,
    AllergyListCreatorComponent,
    AutoLogoutComponent,
    AuthListCreatorComponent,
    AuthorizationListComponent,
    EmailVerificationSnackbarComponent,
    PageNotFoundComponent,
    PolicyViewerComponent,
    MedicalTeamListCreatorComponent,
    IntlPhoneInputComponent,
    BulkEmailListCreatorComponent,
    MedicalHistoryListCreatorComponent,
    MedicationListCreatorComponent,
    MatTabGroupGestureDirective,
    NumberOnlyDirective,
    WhatsappDialogTriggerDirective,
    DraggableOverlayPaneDirective,
    ResizableOverlayPaneDirective,
    MedicationAutocompleteComponent,
    DemoAccountGeneratorComponent,
    AiAssistantDialogComponent,
    CuraFormReviewDialogComponent,
    HeightInputDirective,
    WeightInputDirective,
    PasswordStrengthComponent,
    TutorialCardComponent,
    TutorialExpansionTargetDirective,
    TutorialHighlightTargetDirective,
    VideoDialogComponent,
    HelpIndexDialogComponent,
    RecoverRecordingDialogComponent,
    CodeSearchSelectorComponent,
    HistoryChartDialogComponent,
    PatientActiveDirective,
    PatientValidationStatusDirective,
    PatientValidationMessagesComponent,
    GroupSpecialtyHideDirective,
    GroupSpecialtyHideStructuralDirective,
    ProviderInitialsTooltipDirective,
    TouchEditDirective,
    SignatureOptionsComponent,
    SignatureWrapperComponent,
    RecipientSearchComponent,
    FailedRecordingsDialogComponent,
    RecipientSearchComponent,
    LabTestSelectorComponent,
    LabResultsDialogComponent,
    LabMonitoringDialogComponent,
    WhatsAppChatDialogComponent,
    MediaSelectorDialogComponent,
    DocumentAttachmentPickerComponent,
    DocumentAttachmentDialogComponent,
    PrescriptionSelectorDialogComponent,
    BillingDocumentSelectorDialogComponent,
    LabOrderConfigDialogComponent,
    RestoreTimelineDialogComponent,
    ExportProgressDialogComponent,
    ConsumableOverrideSelectorComponent,
    TurnstileComponent,
    PatientSelectionDialogComponent,
    UnlinkedResultLinkDialogComponent,
    UnlinkedResultDismissDialogComponent,
    TaskDialogComponent,
    UnlinkedResultPayloadDialogComponent,
    DocumentReviewDialogComponent,
    FindReplacePanelComponent,
    FindReplaceDirective,
    FindReplaceScopeDirective,
    DateRangeMenuComponent,
    RiskSuggestionListComponent,
    RiskItemAddDialogComponent,
    DynamicFormRendererComponent
  ],
  imports: [
    KeepCaretVisibleDirective,
    FitAboveKeyboardDirective,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    // The floating chat window, mounted in the dashboard shell (tasks/messaging-hub-architecture.md M1).
    ChatModule,
    HttpClientModule,
    NgxMaterialTimepickerModule,
    DragDropModule,
    SearchPipe,
    NgxExtendedPdfViewerModule,
    NgxColorsModule,
    NgxChartsModule,
    MatIconModule,
    ...MAT_MODULES
  ],
  exports: [
    NgxExtendedPdfViewerModule,
    DateRangeMenuComponent,
    // E2/F2 — used from PatientModule's templates, so it must be exported, not just declared.
    // The dialog is opened imperatively via MatDialog and needs no export.
    RiskSuggestionListComponent,
    TranscriptProcessingComponent,
    HeaderComponent,
    FormsModule,
    LoaderComponent,
    BackgroundAnimationComponent,
    ImageDialogComponent,
    DashboardComponent,
    SidenavComponent,
    FabMenuComponent,
    InfoDialogComponent,
    AnnouncementDialogComponent,
    SystemStatusDotComponent,
    FileUploadComponent,
    ProcedureCodeSelectorComponent,
    SignaturePadComponent,
    FileGalleryComponent,
    AiDialogComponent,
    AudioUploadComponent,
    RecordingComponent,
    TranscriptInputComponent,
    AllergyListCreatorComponent,
    NgxMaterialTimepickerModule,
    AutoLogoutComponent,
    AuthListCreatorComponent,
    AuthorizationListComponent,
    EmailVerificationSnackbarComponent,
    PageNotFoundComponent,
    PolicyViewerComponent,
    DragDropModule,
    MedicalTeamListCreatorComponent,
    IntlPhoneInputComponent,
    SearchPipe,
    BulkEmailListCreatorComponent,
    MedicalHistoryListCreatorComponent,
    MedicationListCreatorComponent,
    MatTabGroupGestureDirective,
    NumberOnlyDirective,
    WhatsappDialogTriggerDirective,
    DraggableOverlayPaneDirective,
    ResizableOverlayPaneDirective,
    MedicationAutocompleteComponent,
    DemoAccountGeneratorComponent,
    AiAssistantDialogComponent,
    HeightInputDirective,
    WeightInputDirective,
    PasswordStrengthComponent,
    TutorialCardComponent,
    TutorialExpansionTargetDirective,
    TutorialHighlightTargetDirective,
    VideoDialogComponent,
    HelpIndexDialogComponent,
    RecoverRecordingDialogComponent,
    CodeSearchSelectorComponent,
    HistoryChartDialogComponent,
    NgxChartsModule,
    GroupSpecialtyHideDirective,
    GroupSpecialtyHideStructuralDirective,
    PatientActiveDirective,
    PatientValidationStatusDirective,
    PatientValidationMessagesComponent,
    ProviderInitialsTooltipDirective,
    TouchEditDirective,
    SignatureOptionsComponent,
    SignatureWrapperComponent,
    RecipientSearchComponent,
    FailedRecordingsDialogComponent,
    LabTestSelectorComponent,
    LabResultsDialogComponent,
    LabMonitoringDialogComponent,
    WhatsAppChatDialogComponent,
    RestoreTimelineDialogComponent,
    ExportProgressDialogComponent,
    ConsumableOverrideSelectorComponent,
    TurnstileComponent,
    PatientSelectionDialogComponent,
    UnlinkedResultLinkDialogComponent,
    UnlinkedResultDismissDialogComponent,
    TaskDialogComponent,
    UnlinkedResultPayloadDialogComponent,
    DocumentReviewDialogComponent,
    FindReplaceDirective,
    FindReplaceScopeDirective,
    KeepCaretVisibleDirective,
    FitAboveKeyboardDirective,
    DynamicFormRendererComponent,
    // §14.3 - embedded in PatientModule's dialogs and (wave 2) in GroupModule's, so it must be
    // exported and not merely declared.
    DocumentAttachmentPickerComponent,
    // Opened through `MatDialog` rather than written into a host's template, so it is exported for
    // the same reason the other shared dialogs here are: a host module that opens it must be able
    // to see the class, and `DocumentAttachmentService.choose` opens it on behalf of hosts that
    // have no picker at all.
    DocumentAttachmentDialogComponent,
    ...MAT_MODULES,
    ...PIPES,
  ],
  providers: [...PIPES]
})
export class SharedModule {

  private path: string = 'assets/svg-icons';
  constructor(
    private domSanitizer: DomSanitizer,
    public matIconRegistry: MatIconRegistry
  ) {
    this.matIconRegistry
      .addSvgIcon('medic_ai', this.setPath(`${this.path}/medic-ai.svg`));
    this.matIconRegistry
      .addSvgIcon('medic_ai_primary', this.setPath(`${this.path}/medic-ai-green.svg`));
    this.matIconRegistry
      .addSvgIcon('medic_ai_highlight', this.setPath(`${this.path}/medic-ai-purple.svg`));
    this.matIconRegistry
      .addSvgIcon('beta', this.setPath(`${this.path}/beta.svg`));
    this.matIconRegistry
      .addSvgIcon('in_out', this.setPath(`${this.path}/in-out.svg`));
    this.matIconRegistry
      .addSvgIcon('ward_round', this.setPath(`${this.path}/ward-round.svg`));
    this.matIconRegistry
      .addSvgIcon('outpatient_primary', this.setPath(`${this.path}/outpatient-primary.svg`));
    this.matIconRegistry
      .addSvgIcon('inpatient_primary', this.setPath(`${this.path}/inpatient-primary.svg`));
    this.matIconRegistry
      .addSvgIcon('inhaler', this.setPath(`${this.path}/inhaler.svg`));
    this.matIconRegistry
      .addSvgIcon('lotion', this.setPath(`${this.path}/lotion.svg`));
    this.matIconRegistry
      .addSvgIcon('blood_bag', this.setPath(`${this.path}/blood_bag.svg`));
    this.matIconRegistry
      .addSvgIcon('capsule', this.setPath(`${this.path}/capsule.svg`));
    this.matIconRegistry
      .addSvgIcon('pill', this.setPath(`${this.path}/pill.svg`));
    this.matIconRegistry
      .addSvgIcon('suppository', this.setPath(`${this.path}/suppository.svg`));
    this.matIconRegistry
      .addSvgIcon('gum', this.setPath(`${this.path}/gum.svg`));
    this.matIconRegistry
      .addSvgIcon('patch', this.setPath(`${this.path}/patch.svg`));
    this.matIconRegistry
      .addSvgIcon('penset', this.setPath(`${this.path}/penset.svg`));
    this.matIconRegistry
      .addSvgIcon('spray', this.setPath(`${this.path}/spray.svg`));
    this.matIconRegistry
      .addSvgIcon('tube', this.setPath(`${this.path}/tube.svg`));
    this.matIconRegistry
      .addSvgIcon('powder', this.setPath(`${this.path}/powder.svg`));
    this.matIconRegistry
      .addSvgIcon('sachet', this.setPath(`${this.path}/sachet.svg`));
    this.matIconRegistry
      .addSvgIcon('wind', this.setPath(`${this.path}/wind.svg`));
    this.matIconRegistry
      .addSvgIcon('bubble', this.setPath(`${this.path}/bubble.svg`));
    this.matIconRegistry
      .addSvgIcon('kit', this.setPath(`${this.path}/kit.svg`));
    this.matIconRegistry
      .addSvgIcon('scalpel_and_forceps', this.setPath(`${this.path}/scalpel_and_forceps.svg`));
    this.matIconRegistry
      .addSvgIcon('syringe', this.setPath(`${this.path}/syringe.svg`));
    this.matIconRegistry
      .addSvgIcon('whatsapp', this.setPath(`${this.path}/whatsapp.svg`));
  }

  private setPath(url: string): SafeResourceUrl {
    return this.domSanitizer.bypassSecurityTrustResourceUrl(url);
  }
}
